<script setup lang="ts">
import { useIntersectionObserver } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'
import AssinaturaService from './services/AssinaturaService'
import { useMinhasAssinaturasStore } from './store/useMinhasAssinaturasStore'
import type { IAssinatura } from './types/assinatura'
import { useSnackbarStore } from '@/stores/useSnackbarStore'
import { useConfirmDialogStore } from '@/stores/useConfirmDialogStore'
import PlanoService from '@/pages/admin/plano/services/PlanoService'
import FornecedorService from '@/pages/admin/fornecedor/services/FornecedorService'
import { can } from '@/@layouts/plugins/casl'

definePage({
  meta: {
    action: 'read',
    subject: 'assinatura',
  },
})

const store = useMinhasAssinaturasStore()
const snackbarStore = useSnackbarStore()
const confirmDialogStore = useConfirmDialogStore()

const {
  items,
  loading,
  searchTerm,
  estatisticas,
} = storeToRefs(store)

const {
  loadItems,
  loadMore,
  search: onSearch,
  searchAgain: onSearchAgain,
  resetSearch,
  carregarEstatisticas,
  cancelar,
  reativar,
  verificarStatus,
} = store

// States locais
const filtros = ref({
  busca: null as string | null,
  status: null as string | null,
})

// Modal de assinatura gratuita
const modalAssinaturaGratuita = ref(false)
const loadingAssinaturaGratuita = ref(false)

const formAssinaturaGratuita = ref({
  fornecedor_id: null as string | null,
  plano_id: null as string | null,
})

const fornecedoresList = ref<any[]>([])
const planosList = ref<any[]>([])

const abrirModalAssinaturaGratuita = async () => {
  modalAssinaturaGratuita.value = true

  // Carregar fornecedores e planos
  try {
    const [fornecedoresResponse, planosResponse] = await Promise.all([
      FornecedorService.fetchAll({
        per_page: 100000,
      }),
      PlanoService.fetchAll({
        per_page: 100000,
      }),
    ])

    fornecedoresList.value = fornecedoresResponse.data.data || []
    planosList.value = planosResponse.data.data || []
  }
  catch (error) {
    console.error('Erro ao carregar dados:', error)
    snackbarStore.showSnackbar({
      text: 'Erro ao carregar fornecedores e planos',
      color: 'error',
    })
  }
}

const fecharModalAssinaturaGratuita = () => {
  modalAssinaturaGratuita.value = false
  formAssinaturaGratuita.value = {
    fornecedor_id: null,
    plano_id: null,
  }
}

const ativarAssinaturaGratuita = async () => {
  if (!formAssinaturaGratuita.value.fornecedor_id || !formAssinaturaGratuita.value.plano_id) {
    snackbarStore.showSnackbar({
      text: 'Selecione o fornecedor e o plano',
      color: 'warning',
    })

    return
  }

  loadingAssinaturaGratuita.value = true

  try {
    await AssinaturaService.ativarAssinaturaGratuita(
      formAssinaturaGratuita.value.fornecedor_id,
      formAssinaturaGratuita.value.plano_id,
    )

    snackbarStore.showSnackbar({
      text: 'Assinatura gratuita ativada com sucesso',
      color: 'success',
    })

    fecharModalAssinaturaGratuita()
    await onSearchAgain()
    await carregarEstatisticas()
  }
  catch (error: any) {
    snackbarStore.showSnackbar({
      text: error.response?.data?.message || 'Erro ao ativar assinatura gratuita',
      color: 'error',
    })
  }
  finally {
    loadingAssinaturaGratuita.value = false
  }
}

// Status options
const statusOptions = [
  { title: 'Ativa', value: 'ATIVA' },
  { title: 'Inativa', value: 'INATIVA' },
  { title: 'Cancelada', value: 'CANCELADA' },
  { title: 'Vencida', value: 'VENCIDA' },
]

// Computed para tipagem correta
const assinaturas = computed(() => items.value as unknown as IAssinatura[] ?? [])

// Scroll infinito usando Intersection Observer
const infiniteScroll = ref<HTMLElement>()

const { stop } = useIntersectionObserver(
  infiniteScroll,
  ([{ isIntersecting }]: any) => {
    if (isIntersecting && !loading.value.items)
      loadMore()
  },
)

// Busca com filtros
const buscarAssinaturas = async () => {
  // Atualizar searchTerm com os filtros ativos
  const filtrosAtivos: any = {}

  if (filtros.value.busca)
    filtrosAtivos.search = filtros.value.busca

  if (filtros.value.status)
    filtrosAtivos.status = filtros.value.status

  searchTerm.value = filtrosAtivos
  await onSearch()
}

// Limpar filtros
const limparFiltros = async () => {
  filtros.value = { busca: null, status: null }
  await resetSearch()
}

// Methods
const cancelarAssinatura = (assinatura: IAssinatura) => {
  confirmDialogStore.showConfirmDialog(
    `Tem certeza que deseja cancelar a assinatura de ${assinatura.fornecedor?.nome_fantasia}?`,
    'Cancelar Assinatura',
    'Manter Ativa',
    false,
    async () => {
      try {
        await cancelar(assinatura.id)
        snackbarStore.showSnackbar({
          text: 'Assinatura cancelada com sucesso',
          color: 'success',
        })
        await onSearchAgain()
      }
      catch (error) {
        snackbarStore.showSnackbar({
          text: 'Erro ao cancelar assinatura',
          color: 'error',
        })
      }
    },
  )
}

const reativarAssinatura = (assinatura: IAssinatura) => {
  confirmDialogStore.showConfirmDialog(
    `Tem certeza que deseja reativar a assinatura de ${assinatura.fornecedor?.nome_fantasia}?`,
    'Reativar',
    'Cancelar',
    false,
    async () => {
      try {
        await reativar(assinatura.id)
        snackbarStore.showSnackbar({
          text: 'Assinatura reativada com sucesso',
          color: 'success',
        })
        await onSearchAgain()
      }
      catch (error) {
        snackbarStore.showSnackbar({
          text: 'Erro ao reativar assinatura',
          color: 'error',
        })
      }
    },
  )
}

const verificarStatusAssinatura = async (assinatura: IAssinatura) => {
  try {
    await verificarStatus(assinatura.id)
    snackbarStore.showSnackbar({
      text: 'Status verificado com sucesso',
      color: 'info',
    })
    await onSearchAgain()
  }
  catch (error) {
    snackbarStore.showSnackbar({
      text: 'Erro ao verificar status',
      color: 'error',
    })
  }
}

// Helpers
const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value)
}

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString('pt-BR')
}

const getStatusColor = (status: string) => {
  if (status === 'ATIVA')
    return 'success'
  if (status === 'INATIVA')
    return 'warning'
  if (status === 'CANCELADA')
    return 'error'
  if (status === 'VENCIDA')
    return 'error'

  return 'secondary'
}

const getStatusIcon = (status: string) => {
  if (status === 'ATIVA')
    return 'tabler-check'
  if (status === 'INATIVA')
    return 'tabler-pause'
  if (status === 'CANCELADA')
    return 'tabler-x'
  if (status === 'VENCIDA')
    return 'tabler-clock'

  return 'tabler-help'
}

// Lifecycle
onMounted(async () => {
  await Promise.all([
    loadItems(),
    carregarEstatisticas(),
  ])
})

onBeforeRouteLeave(() => {
  stop()
  store.$reset()
})
</script>

<template>
  <VContainer fluid>
    <!-- Header -->
    <VRow>
      <VCol cols="12">
        <div class="mb-6 d-flex align-center justify-space-between">
          <div>
            <h1 class="mb-2 text-h4 font-weight-bold text-primary">
              Minhas Assinaturas
            </h1>
            <p class="mb-0 text-subtitle-1 text-medium-emphasis">
              Gerencie suas assinaturas ativas e históricas
            </p>
          </div>

          <div class="gap-2 d-flex">
            <VBtn
              v-if="can('all', 'manager')"
              color="success"
              prepend-icon="tabler-gift"
              @click="abrirModalAssinaturaGratuita"
            >
              Ativar Gratuita
            </VBtn>

            <VBtn
              color="primary"
              prepend-icon="tabler-refresh"
              variant="tonal"
              @click="onSearchAgain"
            >
              Atualizar
            </VBtn>
          </div>
        </div>
      </VCol>
    </VRow>

    <!-- Estatísticas -->
    <VRow class="mb-6">
      <VCol
        cols="12"
        sm="6"
        md="3"
      >
        <VCard class="text-center">
          <VCardText>
            <VAvatar
              color="success"
              size="56"
              class="mb-4"
            >
              <VIcon
                icon="tabler-check"
                size="32"
              />
            </VAvatar>
            <h3 class="mb-1 text-h4 text-high-emphasis">
              {{ estatisticas?.total_ativas || 0 }}
            </h3>
            <p class="mb-0 text-sm text-medium-emphasis">
              Assinaturas Ativas
            </p>
          </VCardText>
        </VCard>
      </VCol>

      <VCol
        cols="12"
        sm="6"
        md="3"
      >
        <VCard class="text-center">
          <VCardText>
            <VAvatar
              color="info"
              size="56"
              class="mb-4"
            >
              <VIcon
                icon="tabler-cash"
                size="32"
              />
            </VAvatar>
            <h3 class="mb-1 text-h4 text-high-emphasis">
              {{ formatCurrency(estatisticas?.valor_total_mensal || 0) }}
            </h3>
            <p class="mb-0 text-sm text-medium-emphasis">
              Valor Total Mensal
            </p>
          </VCardText>
        </VCard>
      </VCol>

      <VCol
        cols="12"
        sm="6"
        md="3"
      >
        <VCard class="text-center">
          <VCardText>
            <VAvatar
              color="warning"
              size="56"
              class="mb-4"
            >
              <VIcon
                icon="tabler-clock"
                size="32"
              />
            </VAvatar>
            <h3 class="mb-1 text-h4 text-high-emphasis">
              {{ estatisticas?.vencendo_em_7_dias || 0 }}
            </h3>
            <p class="mb-0 text-sm text-medium-emphasis">
              Vencendo em 7 dias
            </p>
          </VCardText>
        </VCard>
      </VCol>

      <VCol
        cols="12"
        sm="6"
        md="3"
      >
        <VCard class="text-center">
          <VCardText>
            <VAvatar
              color="error"
              size="56"
              class="mb-4"
            >
              <VIcon
                icon="tabler-x"
                size="32"
              />
            </VAvatar>
            <h3 class="mb-1 text-h4 text-high-emphasis">
              {{ (estatisticas?.total_canceladas || 0) + (estatisticas?.total_vencidas || 0) }}
            </h3>
            <p class="mb-0 text-sm text-medium-emphasis">
              Canceladas/Vencidas
            </p>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Filtros -->
    <VCard class="mb-6">
      <VCardItem>
        <VCardTitle class="text-h6">
          <VIcon
            icon="tabler-filter"
            class="me-2"
          />
          Filtros
        </VCardTitle>
      </VCardItem>

      <VCardText>
        <VRow>
          <VCol
            cols="12"
            md="6"
          >
            <AppTextField
              v-model="filtros.busca"
              label="Buscar por fornecedor"
              placeholder="Digite o nome do fornecedor"
              prepend-inner-icon="tabler-search"
              clearable
              density="comfortable"
              @input="buscarAssinaturas"
            />
          </VCol>

          <VCol
            cols="12"
            md="6"
          >
            <AppSelect
              v-model="filtros.status"
              label="Status"
              placeholder="Selecione o status"
              :items="statusOptions"
              clearable
              density="comfortable"
              @update:model-value="buscarAssinaturas"
            />
          </VCol>
        </VRow>
      </VCardText>
    </VCard>

    <!-- Lista de Assinaturas -->
    <VRow>
      <VCol
        v-for="assinatura in assinaturas"
        :key="assinatura.id"
        cols="12"
        md="6"
        xl="4"
      >
        <VCard
          class="h-100"
          :class="{
            'border-success': assinatura.status === 'ATIVA',
            'border-warning': assinatura.status === 'INATIVA',
            'border-error': ['CANCELADA', 'VENCIDA'].includes(assinatura.status),
          }"
        >
          <VCardItem class="pb-0">
            <template #prepend>
              <VAvatar
                :color="getStatusColor(assinatura.status)"
                size="40"
              >
                <VIcon
                  :icon="getStatusIcon(assinatura.status)"
                  size="20"
                />
              </VAvatar>
            </template>

            <VCardTitle class="text-h6">
              {{ assinatura.fornecedor?.nome_fantasia || 'Fornecedor não encontrado' }}
            </VCardTitle>

            <template #append>
              <div class="gap-2 d-flex align-center">
                <VChip
                  v-if="assinatura.tipo === 'gratuita'"
                  color="info"
                  size="small"
                  label
                  prepend-icon="tabler-gift"
                >
                  Gratuita
                </VChip>
                <VChip
                  :color="getStatusColor(assinatura.status)"
                  size="small"
                  label
                >
                  {{ assinatura.status }}
                </VChip>
              </div>
            </template>
          </VCardItem>

          <VCardText class="pb-0">
            <VList
              lines="one"
              density="compact"
            >
              <VListItem
                prepend-icon="tabler-tag"
                :title="assinatura.plano?.titulo || 'Plano não encontrado'"
                subtitle="Plano"
              />

              <VListItem
                prepend-icon="tabler-cash"
                :title="formatCurrency(assinatura.valor || 0)"
                subtitle="Valor Mensal"
              />

              <VListItem
                prepend-icon="tabler-calendar"
                :title="formatDate(assinatura.data_vencimento)"
                subtitle="Data de Vencimento"
              />

              <VListItem
                v-if="assinatura.observacoes"
                s
                prepend-icon="tabler-file-text"
                :title="assinatura.observacoes"
                subtitle="Observações"
              />
            </VList>
          </VCardText>

          <VSpacer />

          <VCardActions class="pt-0 pa-4">
            <VSpacer />
            <VBtn
              v-if="assinatura.status === 'ATIVA'"
              color="error"
              variant="outlined"
              prepend-icon="tabler-cancel"
              @click="cancelarAssinatura(assinatura)"
            >
              Cancelar
            </VBtn>

            <VBtn
              v-if="['CANCELADA', 'VENCIDA'].includes(assinatura.status)"
              color="success"
              variant="outlined"
              prepend-icon="tabler-refresh"
              @click="reativarAssinatura(assinatura)"
            >
              Reativar
            </VBtn>
          </VCardActions>
        </VCard>
      </VCol>
    </VRow>

    <!-- Scroll infinito trigger -->
    <div
      ref="infiniteScroll"
      class="justify-center py-8 d-flex"
    >
      <VProgressCircular
        v-if="loading.items"
        indeterminate
        color="primary"
      />
    </div>

    <!-- Empty State -->
    <VRow v-if="!loading.items && assinaturas.length === 0">
      <VCol
        cols="12"
        class="py-12 text-center"
      >
        <VAvatar
          color="grey-lighten-2"
          size="80"
          class="mb-6"
        >
          <VIcon
            icon="tabler-file-text"
            size="40"
            color="grey-darken-1"
          />
        </VAvatar>

        <h3 class="mb-4 text-h5 text-high-emphasis">
          Nenhuma assinatura encontrada
        </h3>

        <p class="mb-6 text-body-1 text-medium-emphasis">
          {{ filtros.busca || filtros.status
            ? 'Tente ajustar os filtros para encontrar suas assinaturas'
            : 'Você ainda não possui assinaturas registradas'
          }}
        </p>

        <VBtn
          v-if="filtros.busca || filtros.status"
          color="primary"
          variant="outlined"
          prepend-icon="tabler-filter-off"
          @click="limparFiltros"
        >
          Limpar Filtros
        </VBtn>
      </VCol>
    </VRow>

    <!-- Modal de Assinatura Gratuita -->
    <VDialog
      v-model="modalAssinaturaGratuita"
      max-width="600"
      persistent
    >
      <VCard>
        <VCardTitle class="pa-6 d-flex align-center justify-space-between bg-primary">
          <div class="gap-3 d-flex align-center">
            <VIcon
              icon="tabler-gift"
              size="28"
              color="white"
            />
            <span class="text-white text-h5">
              Ativar Assinatura Gratuita
            </span>
          </div>
          <VBtn
            icon="tabler-x"
            variant="text"
            size="small"
            color="white"
            @click="fecharModalAssinaturaGratuita"
          />
        </VCardTitle>

        <VCardText class="pa-6">
          <VAlert
            color="info"
            variant="tonal"
            class="mb-6"
          >
            <div class="gap-3 d-flex align-start">
              <VIcon icon="tabler-info-circle" />
              <div>
                <div class="mb-2 font-weight-medium">
                  Assinatura Gratuita
                </div>
                <div class="text-sm">
                  Esta assinatura não gera cobranças e tem validade indeterminada.
                  Ideal para testes, parcerias ou casos especiais.
                </div>
              </div>
            </div>
          </VAlert>

          <VAlert
            color="warning"
            variant="tonal"
            class="mb-6"
          >
            <div class="gap-3 d-flex align-start">
              <VIcon icon="tabler-alert-triangle" />
              <div>
                <div class="mb-2 font-weight-medium">
                  Atenção
                </div>
                <div class="text-sm">
                  Se o fornecedor já possui uma assinatura ativa, ela será automaticamente cancelada
                  e substituída pela assinatura gratuita.
                </div>
              </div>
            </div>
          </VAlert>

          <VRow>
            <VCol cols="12">
              <VAutocomplete
                v-model="formAssinaturaGratuita.fornecedor_id"
                :items="fornecedoresList"
                item-title="nome_fantasia"
                item-value="id"
                label="Selecione o Fornecedor"
                placeholder="Digite para buscar..."
                prepend-inner-icon="tabler-building"
                variant="outlined"
                :disabled="loadingAssinaturaGratuita"
                clearable
              />
            </VCol>

            <VCol cols="12">
              <VSelect
                v-model="formAssinaturaGratuita.plano_id"
                :items="planosList"
                item-title="titulo"
                item-value="id"
                label="Selecione o Plano"
                prepend-inner-icon="tabler-package"
                variant="outlined"
                :disabled="loadingAssinaturaGratuita"
                clearable
              />
            </VCol>
          </VRow>
        </VCardText>

        <VDivider />

        <VCardActions class="pa-6">
          <VSpacer />
          <VBtn
            variant="outlined"
            :disabled="loadingAssinaturaGratuita"
            @click="fecharModalAssinaturaGratuita"
          >
            Cancelar
          </VBtn>
          <VBtn
            color="success"
            prepend-icon="tabler-check"
            :loading="loadingAssinaturaGratuita"
            @click="ativarAssinaturaGratuita"
          >
            Ativar Assinatura
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </VContainer>
</template>

<style scoped>
.border-success {
  border-inline-start: 4px solid rgb(var(--v-theme-success)) !important;
}

.border-warning {
  border-inline-start: 4px solid rgb(var(--v-theme-warning)) !important;
}

.border-error {
  border-inline-start: 4px solid rgb(var(--v-theme-error)) !important;
}

.text-primary {
  color: rgb(var(--v-theme-primary)) !important;
}
</style>
